/**
 * Optional free-form resource tags (Azure rules; the portal-managed tags product /
 * environment / deployment_id / managed_by always win and are added server-side).
 */

export interface AppriseTagRow {
  key: string
  value: string
}

export const MAX_TAGS = 50
export const TAG_KEY_MAX = 512
export const TAG_VALUE_MAX = 256
const TAG_KEY_FORBIDDEN = /[<>%&\\?/]/
const RESERVED_PREFIXES = ['azure', 'microsoft', 'windows']

/** A row counts as a tag unless both name and value are blank (blank rows are ignored). */
export const isBlankTagRow = (r: AppriseTagRow): boolean => !r.key.trim() && !r.value.trim()

/** Rows → the payload map (blank rows and rows without a name are dropped; names / values trimmed). */
export function tagRowsToRecord(rows: AppriseTagRow[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const r of rows) {
    const key = r.key.trim()
    if (key) out[key] = r.value.trim()
  }
  return out
}

export function tagRecordToRows(tags: Record<string, string> | undefined): AppriseTagRow[] {
  return Object.entries(tags ?? {}).map(([key, value]) => ({ key, value }))
}

/** Errors keyed "operations.tags" (count) and "operations.tags.<row>.key|value". */
export function validateTagRows(rows: AppriseTagRow[]): Record<string, string> {
  const errors: Record<string, string> = {}
  const seen = new Map<string, number>()
  let count = 0
  rows.forEach((r, i) => {
    if (isBlankTagRow(r)) return
    count += 1
    const key = r.key.trim()
    if (!key) errors[`operations.tags.${i}.key`] = 'Tag name is required'
    else if (key.length > TAG_KEY_MAX) errors[`operations.tags.${i}.key`] = `At most ${TAG_KEY_MAX} characters`
    else if (TAG_KEY_FORBIDDEN.test(key)) errors[`operations.tags.${i}.key`] = 'A tag name cannot contain < > % & \\ ? /'
    else if (RESERVED_PREFIXES.some((p) => key.toLowerCase().startsWith(p)))
      errors[`operations.tags.${i}.key`] = 'Names starting with azure, microsoft or windows are reserved'
    else if (seen.has(key.toLowerCase())) errors[`operations.tags.${i}.key`] = `Tag names are case-insensitive — already used by tag ${(seen.get(key.toLowerCase()) as number) + 1}`
    else seen.set(key.toLowerCase(), i)
    if (r.value.trim().length > TAG_VALUE_MAX) errors[`operations.tags.${i}.value`] = `At most ${TAG_VALUE_MAX} characters`
  })
  if (count > MAX_TAGS) errors['operations.tags'] = `At most ${MAX_TAGS} tags`
  return errors
}

/** Same rules for an already-built map (API-shaped payload). */
export function validateTagRecord(tags: Record<string, string>): Record<string, string> {
  return validateTagRows(tagRecordToRows(tags))
}
